import React, { useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Stack, Switch, TableCell, Tooltip } from "@mui/material";
import { Edit } from "@mui/icons-material";
import { formatMediumAddress } from "../../../../../model/Medium";
import { OfferMedium } from "../../../../../model/OfferMedium";
import PhotoUrlFactory from "../../../../../infrastructure/url-factory/PhotoUrlFactory";
import { CopyTextToClipboard } from "../../../../../action/copyTextToClipboard";
import MediumIdTableCell from "../../common/MediumIdTableCell";
import AvailabilitySubCell from "./AvailabilitySubCell";
import formatFootfall from "../../../../utils/formatFootfall";
import { UpdateManyAvailabilities } from "../../../../../action/updateManyAvailabilities";
import useTrackPromise from "../../../../hooks/useTrackPromise";
import getOfferId from "../../../../../core/getOfferId";
import ProductionPriceEditionDialog from "./ProductionPriceEditionDialog";

const DataTableRowCells: React.FC<MainDataTableCellsProps> = props => {
    const { medium } = props;

    const offerId = getOfferId();

    const [updateManyAvailabilities, , statusUpdateManyAvailabilities] = useTrackPromise(props.updateManyAvailabilities);

    const availabilityIds = useMemo(
        () => medium.availabilities.map(availability => availability.id),
        [medium.availabilities]
    );

    const [recommended, setRecommended] = useState(medium.recommended || false);
    const [productionPriceDialogOpen, setProductionPriceDialogOpen] = useState(false);

    const onEditProductionPriceClick = useCallback(e => {
        e.stopPropagation();
        setProductionPriceDialogOpen(true);
    }, []);

    const onToggleRecommendedClick = useCallback(e => {
        e.stopPropagation();
        setRecommended(recommended => {
            updateManyAvailabilities(
                offerId,
                [],
                availabilityIds,
                { recommended: !recommended }
            ).catch(() => setRecommended(recommended));
            return !recommended;
        });
    }, [offerId, availabilityIds]);

    return (
        <>
            <TableCell>
                <Tooltip
                    arrow
                    placement='right'
                    title={
                        <Box p={ 1 } bgcolor='#ffffff'>
                            <img src={ props.photoUrlFactory.urlForTooltip(medium.primaryPhotoId) } loading='lazy'/>
                        </Box>
                    }
                >
                    <img
                        width="60"
                        height="40"
                        src={ props.photoUrlFactory.urlForList(medium.primaryPhotoId) }
                        loading='lazy'
                    />
                </Tooltip>
            </TableCell>
            <MediumIdTableCell copyTextToClipboard={ props.copyTextToClipboard } { ...medium }/>
            <TableCell>{ medium.town }</TableCell>
            <TableCell>{ formatMediumAddress(medium) }</TableCell>
            <TableCell>{ medium.roads?.join(', ') }</TableCell>
            <TableCell>{ medium.kind.fullName }</TableCell>
            <TableCell>
                {
                    medium.availabilities.map(availability => (
                        <AvailabilitySubCell
                            key={ availability.id }
                            mediumId={ medium.mediumId }
                            availability={ availability }
                            onEdit={ props.onEdit }
                            updateManyAvailabilities={ props.updateManyAvailabilities }
                        />
                    ))
                }
            </TableCell>
            <TableCell>
                { medium.productionCost !== null &&
                    <Box sx={{whiteSpace: 'nowrap'}}>Koszt: { medium.productionCost } zł</Box>
                }
                { (medium.productionPrice !== null || medium.productionCost !== null) &&
                    <Stack direction='row' alignItems='center' whiteSpace='nowrap'>
                        <IconButton size='small' onClick={ onEditProductionPriceClick }>
                            <Edit fontSize='small'/>
                        </IconButton>
                        <Box>Cena: { medium.productionPrice ?? '-' } zł</Box>
                    </Stack>
                }
            </TableCell>
            <TableCell>{ medium.illuminated ? 'Tak' : 'Nie' }</TableCell>
            <TableCell>{ formatFootfall(medium) }</TableCell>
            <TableCell>
                <Switch
                    onClick={ onToggleRecommendedClick }
                    size='small'
                    checked={ recommended }
                    disabled={ statusUpdateManyAvailabilities.pending() }
                />
            </TableCell>
            {
                productionPriceDialogOpen &&
                <ProductionPriceEditionDialog
                    onClose={ () => setProductionPriceDialogOpen(false) }
                    onSubmit={
                        values => props.updateManyAvailabilities(
                            offerId,
                            [medium.mediumId],
                            [],
                            values
                        ).then(() => {
                            setProductionPriceDialogOpen(false);
                            props.onEdit();
                        })
                    }
                />
            }
        </>
    );
}
export default React.memo(DataTableRowCells);

interface MainDataTableCellsProps {
    readonly medium: OfferMedium;
    readonly photoUrlFactory: PhotoUrlFactory;
    readonly copyTextToClipboard: CopyTextToClipboard;
    readonly onEdit: () => void;
    readonly updateManyAvailabilities: UpdateManyAvailabilities;
}